import type { ProgressLogEntry, ProjectExecute } from "../types/ead-types.ts";
import {
  isLoginPhaseCompleteStatus,
  isLiveProjectRunStatus,
  ProjectRunStatus,
  readProjectRunStatus,
  storedProjectRunStatus,
} from "./run-status.ts";

export type LoginPhaseStatus = "missing" | "pending" | "success" | "failed";

/** Minimum Phase 1 login attempt window before UI may show login phase failed. */
export const LOGIN_INIT_GRACE_MIN_MINUTES = 30;

export function loginInitGraceBudgetMs(ex: ProjectExecute): number {
  const budgetMin = Number(ex.timeBudgetMinutes || 0);
  const mins = budgetMin > 0 ? Math.max(LOGIN_INIT_GRACE_MIN_MINUTES, budgetMin) : LOGIN_INIT_GRACE_MIN_MINUTES;
  return mins * 60_000;
}

export function loginInitGraceExpired(ex: ProjectExecute, nowMs = Date.now()): boolean {
  if (readProjectRunStatus(ex) !== ProjectRunStatus.Initializing) return false;
  const started = Number(ex.startTime ?? (ex as { createdAt?: number }).createdAt ?? 0);
  if (!Number.isFinite(started) || started <= 0) return false;
  return nowMs - started >= loginInitGraceBudgetMs(ex);
}

function hasLoginAttemptActivity(log: ProgressLogEntry[]): boolean {
  if (countLoginRelatedScreenshotUrls(log) > 0) return true;
  for (const entry of log) {
    const blob = entryBlob(entry);
    if (!blob) continue;
    if (
      hasTextMarker(blob, LOGIN_SUCCESS_TEXT_MARKERS)
      || hasTextMarker(blob, LOGIN_FAILED_TEXT_MARKERS)
      || hasTextMarker(blob, CREDENTIAL_REQUEST_MARKERS)
    ) {
      return true;
    }
    if (isLoginCheckpoint(entry)) return true;
  }
  return false;
}

/** Gateway inject when chat claims login but formal checkpoint is missing. */
const LOGIN_CHECKPOINT_INJECT_MARKERS = [
  "[ead-login-checkpoint:v1]",
  "formal login checkpoint required",
  "submit formal login checkpoint",
] as const;

/** Gateway-injected prompts that only appear after login (Phase 2+). */
const POST_LOGIN_SYSTEM_MARKERS = [
  "[pfm-post-login-skills:v1]",
  "initialization step 2 (mandatory after login",
  "pfm structure rules (for viewable mindmap)",
  "pfm snapshot — **top priority",
  "operator visibility — screenshot cadence",
  "pfm exploration screenshot cadence",
] as const;

/** Gateway exploration kickoff (init finished — not Step 2 setup prompts). */
const EXPLORATION_KICKOFF_MARKERS = [
  "login confirmed. initialization complete",
  "login confirmed. **running phase** starts now.",
  "[ead-running-bootstrap:v1]",
  "initialization complete. start pfm discovery",
  "start pfm discovery on this execution",
] as const;

/** report_running_step titles that indicate Phase 3 exploration (not login / init review). */
const EXPLORATION_STEP_TITLE_MARKERS = [
  "authentication verification",
  "navigate ",
  "exploration",
  "discovery",
  "requirement module",
  "development sprint",
  "test case",
  "test run",
  "update pfm",
  "pfm mindmap",
  "pfm node",
  "deep-dive",
  "deep dive",
  "feature discovery",
  "active exploration",
] as const;

/** Post-login screenshots in the progress log (same bar as gateway login evidence). */
const POST_LOGIN_SCREENSHOTS_FOR_RUNNING = 3;

const INHERITANCE_REVIEW_TITLE_MARKERS = [
  "pfm inheritance review (initialization)",
  "previous result",
  "previous results",
  "inherited pfm",
  "pfm inheritance",
] as const;

const LOGIN_SUCCESS_TEXT_MARKERS = [
  "login successful",
  "login was successful",
  "login is confirmed",
  "login confirmed",
  "logged in successfully",
  "now logged in",
  "sign in successful",
  "signed in successfully",
  "authenticated view",
  "post-login view",
  "starting phase 2",
  "phase 2 exploration",
  "begin discovery",
  "begin active feature discovery",
  "ready to explore",
  "home dashboard",
  "#/banner",
  "access_token",
] as const;

const LOGIN_FAILED_TEXT_MARKERS = [
  "login failed",
  "sign in failed",
  "authentication failed",
  "invalid password",
  "invalid username",
  "invalid user name or password",
  "captcha failed",
  "wrong password",
] as const;

const CREDENTIAL_REQUEST_MARKERS = [
  "waiting for your login",
  "username and password",
  "provide credentials",
  "need your login",
  "otp",
  "one-time password",
  "two-factor",
] as const;

export function isLoginCheckpoint(entry: ProgressLogEntry): boolean {
  if (entry.kind !== "tool_use") return false;
  if ((entry.toolName || "") !== "report_running_step") return false;
  const ti = (entry.toolInput || {}) as Record<string, unknown>;
  const title = String(ti.title || "").trim().toLowerCase();
  return (
    title.includes("login pfm map (interactive)") || title.includes("login to ead explore")
  );
}

const FORMAL_LOGIN_MIN_SCREENSHOTS = 3;
/** Distinct login screenshots required before UI can leave Initializing (gateway-aligned). */
export const LOGIN_SCREENSHOTS_REQUIRED = FORMAL_LOGIN_MIN_SCREENSHOTS;

export function countLoginRelatedScreenshotUrls(log: ProgressLogEntry[]): number {
  const urls = new Set<string>();
  for (const entry of log) {
    if (entry.kind !== "tool_use" || (entry.toolName || "") !== "report_running_step") continue;
    if (!isLoginCheckpoint(entry)) continue;
    const ti = (entry.toolInput || {}) as Record<string, unknown>;
    const thumbs = ti.thumbnail_urls;
    if (Array.isArray(thumbs)) {
      for (const u of thumbs) {
        const s = String(u || "").trim();
        if (s) urls.add(s);
      }
    }
    const imageUrl = String(entry.imageUrl || "").trim();
    const thumbUrl = String(entry.thumbnailUrl || "").trim();
    if (imageUrl) urls.add(imageUrl);
    if (thumbUrl) urls.add(thumbUrl);
  }
  return urls.size;
}

function nonemptyThumbnailUrls(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((u) => String(u || "").trim()).filter(Boolean);
}

/** True when a formal login checkpoint with enough screenshots exists in the progress log. */
export function hasFormalLoginCheckpointInLog(
  log: ProgressLogEntry[],
  minScreenshots: number = FORMAL_LOGIN_MIN_SCREENSHOTS,
): boolean {
  const totalUrls = new Set<string>();
  for (const entry of [...log].reverse()) {
    if (entry.kind !== "tool_use" || (entry.toolName || "") !== "report_running_step") continue;
    if (!isLoginCheckpoint(entry)) continue;
    const ti = (entry.toolInput || {}) as Record<string, unknown>;
    const loginPhase = String(ti.login_phase_status || "").trim().toLowerCase();
    const loginOk = loginPhase === "success" || ti.login_success === true;
    if (!loginOk) continue;
    const thumbs = nonemptyThumbnailUrls(ti.thumbnail_urls);
    if (thumbs.length >= minScreenshots) return true;
    thumbs.forEach((u) => totalUrls.add(u));
    const imageUrl = String(entry.imageUrl || "").trim();
    const thumbUrl = String(entry.thumbnailUrl || "").trim();
    if (imageUrl) totalUrls.add(imageUrl);
    if (thumbUrl) totalUrls.add(thumbUrl);
    if (totalUrls.size >= minScreenshots) return true;
  }
  return false;
}

function entryBlob(entry: ProgressLogEntry): string {
  const ti = (entry.toolInput || {}) as Record<string, unknown>;
  return [
    entry.text,
    ti.title,
    ti.description,
    ti.observation,
    ti.finding,
    ti.reasoning,
    ti.decision,
    ti.next_direction,
  ]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function statusFromCheckpoint(entry: ProgressLogEntry): LoginPhaseStatus | null {
  const ti = (entry.toolInput || {}) as Record<string, unknown>;
  const status = String(ti.login_phase_status || "").trim().toLowerCase();
  if (status === "success" || status === "failed" || status === "pending") return status;
  if (ti.login_success === true) return "success";
  if (ti.login_success === false) return "failed";
  if (ti.recovered_by_gateway === true) return "success";
  const initStatus = String(ti.initialization_review_status || "").trim().toLowerCase();
  if (initStatus === "success" || ti.ready_to_explore === true) return "success";
  return null;
}

function hasTextMarker(text: string, markers: readonly string[]): boolean {
  const blob = text.toLowerCase();
  return markers.some((marker) => blob.includes(marker));
}

function isInheritanceReviewStep(entry: ProgressLogEntry): boolean {
  if (entry.kind !== "tool_use" || (entry.toolName || "") !== "report_running_step") {
    return false;
  }
  const ti = (entry.toolInput || {}) as Record<string, unknown>;
  const title = String(ti.title || "").trim().toLowerCase();
  const textBlob = [
    ti.description,
    ti.observation,
    ti.finding,
    ti.reasoning,
    ti.decision,
    ti.next_direction,
  ]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .trim()
    .toLowerCase();
  if (INHERITANCE_REVIEW_TITLE_MARKERS.some((m) => title.includes(m))) return true;
  if (textBlob.includes("pfm inheritance review")) return true;
  if (textBlob.includes("previous accumulated")) return true;
  return false;
}

/** Agent narration plans future steps; do not treat as completed phase transitions. */
function isOperatorProgressEvidence(entry: ProgressLogEntry): boolean {
  const kind = String(entry.kind || "").trim().toLowerCase();
  if (kind === "assistant" || kind === "user") return false;
  return kind === "system" || kind === "tool_result" || kind === "tool_use";
}

/** True when the run has clearly moved past login (Phase 2+), from gateway/tools only. */
export function hasPostLoginPhaseEvidence(log: ProgressLogEntry[]): boolean {
  for (const entry of log) {
    if (!isOperatorProgressEvidence(entry)) continue;
    const blob = entryBlob(entry);
    if (blob && hasTextMarker(blob, POST_LOGIN_SYSTEM_MARKERS)) return true;
    if ((entry.toolName || "") === "commit_pfm_snapshot") return true;
    if ((entry.toolName || "") === "pfm_node") return true;
    if ((entry.toolName || "") !== "report_running_step") continue;
    const ti = (entry.toolInput || {}) as Record<string, unknown>;
    const title = String(ti.title || "").toLowerCase();
    if (title.includes("pfm inheritance review (initialization)")) {
      const init = String(ti.initialization_review_status || "").toLowerCase();
      if (init === "success" || ti.ready_to_explore === true) return true;
    }
  }
  return false;
}

function hasExplorationKickoffEvidence(log: ProgressLogEntry[]): boolean {
  for (const entry of log) {
    if (!isOperatorProgressEvidence(entry)) continue;
    if ((entry.toolName || "") === "commit_pfm_snapshot") return true;
    if ((entry.toolName || "") === "pfm_node") return true;
    const blob = entryBlob(entry);
    if (blob && hasTextMarker(blob, EXPLORATION_KICKOFF_MARKERS)) return true;
  }
  return false;
}

function isExplorationReportRunningStep(entry: ProgressLogEntry): boolean {
  if (entry.kind !== "tool_use" || (entry.toolName || "") !== "report_running_step") {
    return false;
  }
  if (isLoginCheckpoint(entry) || isInheritanceReviewStep(entry)) return false;
  const ti = (entry.toolInput || {}) as Record<string, unknown>;
  const title = String(ti.title || "").trim().toLowerCase();
  if (!title || title.includes("login pfm map")) return false;
  if (title.includes("pfm inheritance review")) return false;
  return EXPLORATION_STEP_TITLE_MARKERS.some((marker) => title.includes(marker));
}

/** Structured exploration steps after login (not assistant chat). */
function hasActiveExplorationStepEvidence(log: ProgressLogEntry[]): boolean {
  for (const entry of log) {
    if (!isOperatorProgressEvidence(entry)) continue;
    if (isExplorationReportRunningStep(entry)) return true;
  }
  return false;
}

function entryTimestampMs(entry: ProgressLogEntry): number | null {
  const ts = Number(entry.ts);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  return ts < 1e12 ? ts * 1000 : ts;
}

function findLoginSuccessTimestampMs(log: ProgressLogEntry[]): number | null {
  let earliest: number | null = null;
  for (const entry of log) {
    let counts = false;
    if (isLoginCheckpoint(entry) && statusFromCheckpoint(entry) === "success") {
      counts = true;
    } else {
      const blob = entryBlob(entry);
      if (blob && hasTextMarker(blob, LOGIN_SUCCESS_TEXT_MARKERS)) counts = true;
      if (blob && hasTextMarker(blob, EXPLORATION_KICKOFF_MARKERS)) counts = true;
    }
    if (!counts) continue;
    const tsMs = entryTimestampMs(entry);
    if (tsMs == null) continue;
    if (earliest == null || tsMs < earliest) earliest = tsMs;
  }
  if (earliest != null) return earliest;
  for (const entry of log) {
    const blob = entryBlob(entry);
    if (!blob || !hasTextMarker(blob, POST_LOGIN_SYSTEM_MARKERS)) continue;
    const tsMs = entryTimestampMs(entry);
    if (tsMs != null) return tsMs;
  }
  return null;
}

function countDistinctScreenshotsAfterMs(log: ProgressLogEntry[], afterMs: number): number {
  const urls = new Set<string>();
  for (const entry of log) {
    const tsMs = entryTimestampMs(entry);
    if (tsMs != null && tsMs < afterMs) continue;
    for (const url of [entry.imageUrl, entry.thumbnailUrl]) {
      const s = String(url || "").trim();
      if (s) urls.add(s);
    }
    const ti = (entry.toolInput || {}) as Record<string, unknown>;
    const thumbs = ti.thumbnail_urls;
    if (Array.isArray(thumbs)) {
      for (const url of thumbs) {
        const s = String(url || "").trim();
        if (s) urls.add(s);
      }
    }
  }
  return urls.size;
}

/**
 * When tools cannot commit initialization_review_status (e.g. schema errors), post-login
 * screenshot cadence still proves the browser session is in active exploration.
 */
function hasPostLoginScreenshotCadence(log: ProgressLogEntry[]): boolean {
  const loginMs = findLoginSuccessTimestampMs(log) ?? 0;
  return countDistinctScreenshotsAfterMs(log, loginMs) >= POST_LOGIN_SCREENSHOTS_FOR_RUNNING;
}

/**
 * Post-login initialization review (aligns with gateway executor heuristics).
 * Returns success only when Step 2 is done — not when gateway merely injects post-login prompts.
 *
 * Phase model:
 * - Phase 1: login (see resolveLoginPhaseStatus)
 * - Phase 2: inheritance / init review (this function)
 * - Phase 3: exploration → operator label "Running..."
 */
export function resolveInitializationReviewStatus(
  ex: ProjectExecute,
): "missing" | "pending" | "success" {
  if (!isLoginSuccessful(ex)) return "missing";

  if (Number(ex.runningBootstrapFinishedAtMs || 0) > 0) {
    return "success";
  }

  const log = ex.progressLog ?? [];
  // Post-login skills / step-2 prompts mean init has *started*, not finished.
  if (hasExplorationKickoffEvidence(log)) {
    return "success";
  }
  if (hasActiveExplorationStepEvidence(log)) {
    return "success";
  }
  if (hasPostLoginScreenshotCadence(log)) {
    return "success";
  }

  for (const entry of log.slice().reverse()) {
    if (!isInheritanceReviewStep(entry)) continue;
    const ti = (entry.toolInput || {}) as Record<string, unknown>;
    const status = String(ti.initialization_review_status || "").trim().toLowerCase();
    if (status === "success" || ti.ready_to_explore === true) return "success";
    return "pending";
  }

  return "missing";
}

export function isInitializationComplete(ex: ProjectExecute): boolean {
  return resolveInitializationReviewStatus(ex) === "success";
}

/**
 * Infer login outcome from agent narration — DEPRECATED, unused.
 * Login phase UI uses executor PRS only (see resolveLoginPhaseStatus).
 * @deprecated Do not use for phase gates or countdown.
 */
export function inferLoginPhaseFromProgressLog(
  _log: ProgressLogEntry[],
): LoginPhaseStatus | null {
  return null;
}

/** True when executor has set global run status to Running. */
export function isExecutorRunningPhase(ex: ProjectExecute): boolean {
  return storedProjectRunStatus(ex) === ProjectRunStatus.Running;
}

function pickEpochMs(v: unknown): number | null {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Running-phase budget anchor from executor fields only.
 * RUNNING_PHASE_RULE: when stored PRS is Running, always return an anchor.
 */
export function runningPhaseStartingPointMs(ex: ProjectExecute): number | null {
  if (storedProjectRunStatus(ex) !== ProjectRunStatus.Running) return null;

  const runningStart = pickEpochMs(ex.runningPhaseStartedAtMs)
    ?? pickEpochMs((ex as { running_phase_started_at_ms?: number }).running_phase_started_at_ms);
  if (runningStart != null) return runningStart;

  const loginSuccess = pickEpochMs(ex.loginSuccessAtMs)
    ?? pickEpochMs((ex as { login_success_at_ms?: number }).login_success_at_ms);
  if (loginSuccess != null) return loginSuccess;

  const runStart = pickEpochMs(ex.startTime);
  if (runStart != null) return runStart;

  return Date.now();
}

/**
 * When the run budget countdown should start (ms since epoch).
 * RUNNING_PHASE_RULE: stored PRS Running → always non-null anchor; no log/text heuristics.
 */
export function budgetCountdownStartMs(ex: ProjectExecute): number | null {
  return runningPhaseStartingPointMs(ex);
}

/**
 * Login phase for UI display.
 * LOGIN_SUCCESS_CHECK_RULE: success only when projectRunStatus >= Running (LLM promote).
 * While Initializing, chat/tool "login failed" text does NOT mark failed — keep trying
 * until at least LOGIN_INIT_GRACE_MIN_MINUTES elapse without promotion.
 */
export function resolveLoginPhaseStatus(ex: ProjectExecute): LoginPhaseStatus {
  if (isLoginPhaseCompleteStatus(readProjectRunStatus(ex))) return "success";

  const prs = readProjectRunStatus(ex);
  if (prs === ProjectRunStatus.Initializing) {
    if (loginInitGraceExpired(ex)) return "failed";
    return "pending";
  }

  return "missing";
}

/** Operator-facing login phase line in run overview (not raw gate status). */
export function formatLoginPhaseDisplay(
  ex: ProjectExecute,
  status: LoginPhaseStatus = resolveLoginPhaseStatus(ex),
): string {
  if (status === "success") return "success";
  if (status === "failed") return "failed";
  if (readProjectRunStatus(ex) === ProjectRunStatus.Initializing) {
    return "Initializing...";
  }
  return status;
}

export function isLoginSuccessful(ex: ProjectExecute): boolean {
  return resolveLoginPhaseStatus(ex) === "success";
}

/** Operator-facing checklist while the run is in Phase 1 (Initializing). */
export function loginGateOperatorHint(ex: ProjectExecute): string {
  const log = ex.progressLog ?? [];
  const shots = countLoginRelatedScreenshotUrls(log);
  const shotProgress =
    shots >= LOGIN_SCREENSHOTS_REQUIRED
      ? `${LOGIN_SCREENSHOTS_REQUIRED}/${LOGIN_SCREENSHOTS_REQUIRED}`
      : `${shots}/${LOGIN_SCREENSHOTS_REQUIRED}`;

  return (
    "Sign in on the target site, then record login progress with screenshots (one image per activity step). " +
    `(Screenshots: ${shotProgress}.) ` +
    "Exploration starts automatically once sign-in is confirmed."
  );
}

/** @deprecated Use loginGateOperatorHint */
export function loginScreenshotEvidenceHint(log: ProgressLogEntry[] = []): string {
  const n = countLoginRelatedScreenshotUrls(log);
  const progress =
    n >= LOGIN_SCREENSHOTS_REQUIRED
      ? `${LOGIN_SCREENSHOTS_REQUIRED}/${LOGIN_SCREENSHOTS_REQUIRED} received`
      : `${n}/${LOGIN_SCREENSHOTS_REQUIRED} received`;
  return (
    `Login needs ${LOGIN_SCREENSHOTS_REQUIRED} screenshots sent one at a time ` +
    `(separate Activity steps; one image per step: site URL, login attempt, post-login view). ` +
    `Progress: ${progress}. Login success: executor LLM Login Success Check promotes to Running.`
  );
}

export function isAwaitingLoginCredentials(ex: ProjectExecute): boolean {
  if (!isLiveExecution(ex)) return false;
  if (isLoginSuccessful(ex)) return false;
  if (resolveLoginPhaseStatus(ex) === "failed") return false;

  const log = ex.progressLog ?? [];
  for (const entry of log.slice().reverse()) {
    const blob = entryBlob(entry);
    if (!blob) continue;
    if (hasTextMarker(blob, CREDENTIAL_REQUEST_MARKERS)) return true;
    if (isLoginCheckpoint(entry)) {
      const status = statusFromCheckpoint(entry);
      return status === "pending";
    }
  }

  return resolveLoginPhaseStatus(ex) === "pending";
}

function isLiveExecution(ex: ProjectExecute): boolean {
  return isLiveProjectRunStatus(readProjectRunStatus(ex));
}

